/**
 * Primary left navigation. Renders the {@link NAV_ITEMS} model grouped by
 * {@link NAV_SECTIONS}, hiding any entry the live session can't access (RBAC),
 * and marks the active route via TanStack Router's `useRouterState`.
 *
 * ## Why two sections fold
 *
 * Expanded, the rail is ~24 links, which a reader scans as a wall rather than a
 * list. Reference and Automate ({@link COLLAPSIBLE_NAV_SECTIONS}) are the
 * low-traffic halves of it — material you consult, and machinery that runs
 * without you — so they start folded and the rail opens at roughly fourteen
 * entries. Two rules keep the fold from hiding anything that matters: the
 * section holding the ACTIVE route is always expanded (never hide where the user
 * is standing), and the choice is remembered per IDENTITY rather than per
 * browser — display preferences stored app-wide are how one analyst's view came
 * to follow every other user of a shared workstation.
 *
 * ## Why the skip link is here
 *
 * The shell paints sidebar → header → main, and the sidebar is still a long list
 * of links. Without a skip target, reaching page content by keyboard meant
 * tabbing past all of them on every route change. The link has to be the FIRST
 * focusable thing in the document, and this component renders first in the shell
 * — so it lives here rather than in the header, which renders after it. It sits
 * BESIDE the `<aside>`, not inside it: below 768px the aside is `display: none`,
 * and a skip link inside it would vanish with it.
 *
 * ## Why it is two containers below 768px
 *
 * The rail was a fixed 240px column at every width, so on a 375px phone it left
 * about 135px for the page and the Team page's card text wrapped a word per
 * line. Below the `md` breakpoint the same navigation renders inside a left
 * `Sheet` instead, opened from the header's menu button and closed by picking a
 * page. One nav body and ONE fold state serve both, so the two cannot disagree
 * about which sections are open.
 *
 * ## Why active state is more than a class
 *
 * `bg-accent` is invisible to a screen reader, so the active item was announced
 * identically to every other one. `aria-current="page"` is what says which one
 * you are on.
 */
import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { usePermissions } from "@authe/ui-core/permissions";
import { useEntitled, usePlan } from "@authe/ui-core";
import { useRolledOut } from "@authe/ui-core/flags";
import { Sheet, SheetContent, SheetDescription, SheetTitle, cn } from "@authe/ui-core";
import { createScopedStorage, type ScopedStorage } from "@authe/fe-commons/storage";
import { useStorageIdentity } from "@/app/storageIdentity";
import { MAIN_CONTENT_ID } from "@/app/layoutIds";
import {
  APP_NAME,
  APP_TAGLINE,
  COLLAPSIBLE_NAV_SECTIONS,
  NAV_SECTIONS,
  type NavItem,
  type NavSection,
  activeRailTo,
  visibleNavItems,
} from "@/config";

/** A section heading as a DOM id — `aria-labelledby` cannot reference one with spaces. */
function sectionId(section: string): string {
  return `nav-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

/**
 * Scoped-storage name for the folded sections.
 *
 * The VALUE is the list of collapsed section names, not of expanded ones, so
 * "absent" and "expanded" stay distinguishable: a reader who opens both sections
 * stores `[]`, which is a decision, while a reader who has never touched the
 * rail stores nothing and gets the default fold.
 */
const COLLAPSED_SECTIONS_KEY = "nav:collapsed-sections";

/**
 * The folded sections for this identity, defaulting to the two that start folded.
 *
 * Every stored name is re-checked against {@link COLLAPSIBLE_NAV_SECTIONS}, so a
 * renamed or retired section left behind in somebody's browser is ignored rather
 * than folding a section that is not supposed to fold. Wrapped because storage
 * can throw or hand back a stub with no methods (private windows, blocked site
 * data, and this repo's jsdom runner).
 */
function readCollapsed(storage: ScopedStorage): NavSection[] {
  const fallback = [...COLLAPSIBLE_NAV_SECTIONS];
  try {
    const stored = storage.get<unknown>(COLLAPSED_SECTIONS_KEY, fallback);
    if (!Array.isArray(stored)) return fallback;
    return COLLAPSIBLE_NAV_SECTIONS.filter((section) => stored.includes(section));
  } catch {
    return fallback;
  }
}

/**
 * Scoped-storage name for the rail's width: `true` when the reader folded it to
 * icons. Per identity, like the section folds, so one analyst's choice does
 * not follow every other user of a shared workstation.
 */
const RAIL_COLLAPSED_KEY = "nav:rail-collapsed";

function readRailCollapsed(storage: ScopedStorage): boolean {
  try {
    return storage.get<unknown>(RAIL_COLLAPSED_KEY, false) === true;
  } catch {
    return false;
  }
}

export interface SidebarProps {
  /** Below 768px the rail is a drawer; this is whether it is open. */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

/** Tailwind's `md` — the width at and above which the fixed rail is shown. */
const RAIL_MEDIA_QUERY = "(min-width: 768px)";

export function Sidebar({ mobileOpen = false, onMobileOpenChange }: SidebarProps = {}): React.ReactElement {
  const { can } = usePermissions();
  // The packaging half of the same filter: `can` answers "may this person",
  // `entitled` answers "does this customer's plan include it".
  const entitled = useEntitled();
  const { plan } = usePlan();
  const rolledOut = useRolledOut();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const identity = useStorageIdentity();

  // Memoised on the FIELDS, not the object: `useStorageIdentity` builds a fresh
  // literal every render, so keying on it would mint a new keyspace per paint.
  const storage = React.useMemo(
    () => createScopedStorage(identity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId],
  );

  const [collapsed, setCollapsed] = React.useState<NavSection[]>(() => readCollapsed(storage));
  // The whole rail folded to an icon column (56px) — the width the graph pages
  // want back. Labels move to tooltips; the active page stays marked.
  const [railCollapsed, setRailCollapsed] = React.useState<boolean>(() => readRailCollapsed(storage));

  // Re-seed when the identity (and therefore the keyspace) changes — a tenant or
  // user switch must read ITS OWN choice, not keep the previous one on screen.
  React.useEffect(() => {
    setCollapsed(readCollapsed(storage));
    setRailCollapsed(readRailCollapsed(storage));
  }, [storage]);

  const toggleRail = React.useCallback(() => {
    setRailCollapsed((current) => {
      const next = !current;
      try {
        storage.set(RAIL_COLLAPSED_KEY, next);
      } catch {
        // A remembered width is a convenience; losing it must not break the rail.
      }
      return next;
    });
  }, [storage]);

  const toggleSection = React.useCallback(
    (section: NavSection) => {
      const next = collapsed.includes(section)
        ? collapsed.filter((each) => each !== section)
        : [...collapsed, section];
      setCollapsed(next);
      try {
        // Lands at the storage identity's narrowest level (the user, once signed
        // in), so the choice is this person's and not the browser's.
        storage.set(COLLAPSED_SECTIONS_KEY, next);
      } catch {
        // A remembered fold is a convenience; losing it must not break the rail.
      }
    },
    [collapsed, storage],
  );

  const visible = visibleNavItems(can, entitled, rolledOut, undefined, plan?.id);
  const activeTo = activeRailTo(visible, pathname);
  const isActiveItem = (item: NavItem): boolean => item.to === activeTo;

  // Picking a page closes the drawer; so does the window growing past the
  // breakpoint, or a drawer left open on a phone rotated to landscape would
  // reappear over the rail the next time the window narrowed.
  React.useEffect(() => {
    onMobileOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  React.useEffect(() => {
    if (!mobileOpen || typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia(RAIL_MEDIA_QUERY);
    const close = (): void => {
      if (query.matches) onMobileOpenChange?.(false);
    };
    close();
    query.addEventListener("change", close);
    return () => query.removeEventListener("change", close);
  }, [mobileOpen, onMobileOpenChange]);

  // Rendered twice (rail + drawer), so its heading ids take a prefix: the rail
  // stays in the DOM while hidden, and duplicate ids would point the drawer's
  // `aria-controls` / `aria-labelledby` at the invisible copy.
  // `compact`: the icon-only rail. Section folds do not apply to it — every
  // icon is one glance — and names go to tooltips and to the accessible name.
  const renderNavigation = (idPrefix: string, compact = false): React.ReactElement => (
    <>
      <div className={cn("flex items-center gap-2 py-4", compact ? "justify-center px-2" : "px-5")}>
        <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold" title={compact ? APP_NAME : undefined}>
          A
        </div>
        {compact ? null : (
          <div className="leading-tight">
            <div className="text-sm font-semibold text-foreground">{APP_NAME}</div>
            <div className="text-2xs text-muted-foreground">{APP_TAGLINE}</div>
          </div>
        )}
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-2">
        {NAV_SECTIONS.map((section) => {
          const items = visible.filter((i) => i.section === section);
          if (items.length === 0) return null;

          const collapsible = !compact && COLLAPSIBLE_NAV_SECTIONS.includes(section);
          const holdsActive = items.some((item) => isActiveItem(item));
          // The active route wins over a stored fold, in both directions: a
          // reader who folded Automate and then opened Runs sees Automate open,
          // and their fold comes back the moment they navigate away.
          const expanded = !collapsible || holdsActive || !collapsed.includes(section);
          const headingId = `${idPrefix}${sectionId(section)}`;
          const listId = `${headingId}-items`;
          const Chevron = expanded ? ChevronDown : ChevronRight;

          return (
            <div key={section} className="mb-4">
              {collapsible ? (
                <button
                  type="button"
                  id={headingId}
                  aria-expanded={expanded}
                  aria-controls={listId}
                  // Not `disabled`: the control keeps its place in the tab order
                  // and its state stays readable. It simply declines to fold the
                  // section the reader is standing in, and says why on hover.
                  aria-disabled={holdsActive || undefined}
                  title={
                    holdsActive
                      ? `${section} stays open while you are on one of its pages`
                      : undefined
                  }
                  onClick={() => {
                    if (holdsActive) return;
                    toggleSection(section);
                  }}
                  className={cn(
                    "flex w-full items-center gap-1 rounded-md px-3 py-1 pb-1 text-left text-3xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors",
                    holdsActive ? "cursor-default" : "hover:text-foreground",
                  )}
                >
                  <Chevron className="size-3 shrink-0" aria-hidden />
                  <span>{section}</span>
                  {!expanded && (
                    <span className="ml-auto font-normal normal-case tracking-normal tabular-nums">
                      {items.length}
                    </span>
                  )}
                </button>
              ) : (
                <div
                  id={headingId}
                  className={cn(
                    "pb-1 text-3xs font-semibold uppercase tracking-wider text-muted-foreground",
                    compact ? "sr-only" : "px-3",
                  )}
                >
                  {section}
                </div>
              )}

              {/* Named from its own heading, so the links arrive in labelled
                  groups rather than as one undifferentiated list. `hidden` (not
                  an unmounted subtree) is what keeps `aria-controls` pointing at
                  something a screen reader can resolve. */}
              <ul
                id={listId}
                hidden={!expanded}
                aria-labelledby={headingId}
                className="space-y-0.5"
              >
                {items.map((item) => {
                  const active = isActiveItem(item);
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        // The router stamps `aria-current="page"` on any link
                        // whose route prefixes the location, whatever props say.
                        // Every row but the chosen one matches exactly, so only
                        // that row is announced as the current page.
                        activeOptions={active ? undefined : { exact: true }}
                        aria-current={active ? "page" : undefined}
                        aria-label={compact ? item.label : undefined}
                        title={compact ? item.label : undefined}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md py-2 text-sm transition-colors",
                          compact ? "justify-center px-0" : "px-3",
                          active
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                        )}
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className={compact ? "sr-only" : undefined}>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <>
      {/*
        Hidden until focused. The `href` is the real mechanism (it moves the
        document's sequential-navigation point, which a scripted `.focus()`
        alone does not), and the handler adds what a fragment jump cannot: an
        explicit focus on a container that isn't natively focusable.
      */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
        onClick={() => {
          const main = document.getElementById(MAIN_CONTENT_ID);
          if (!main) return;
          if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
          main.focus();
        }}
      >
        Skip to main content
      </a>

      <aside
        aria-label="Sidebar"
        data-collapsed={railCollapsed || undefined}
        className="hidden h-full w-60 shrink-0 flex-col border-r border-border bg-card md:flex data-[collapsed]:w-14"
      >
        {renderNavigation("", railCollapsed)}
        {/* Fold the rail to icons, and back. At the foot, under the links, where
            it is out of the way and the same place in both widths. */}
        <div className={cn("border-t border-border p-2", railCollapsed ? "flex justify-center" : "flex justify-end")}>
          <button
            type="button"
            aria-expanded={!railCollapsed}
            aria-label={railCollapsed ? "Expand navigation" : "Collapse navigation"}
            title={railCollapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={toggleRail}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
          >
            {railCollapsed ? <ChevronsRight className="size-4" aria-hidden /> : <><ChevronsLeft className="size-4" aria-hidden /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Below 768px: the same navigation as a drawer. */}
      <Sheet open={mobileOpen} onOpenChange={(open) => onMobileOpenChange?.(open)}>
        <SheetContent side="left" className="flex w-72 max-w-[85vw] flex-col gap-0 bg-card p-0 md:hidden">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Pages of the dashboard</SheetDescription>
          {renderNavigation("drawer-")}
        </SheetContent>
      </Sheet>
    </>
  );
}
